import { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageCarousel from '../../../components/feature/ImageCarousel';

interface ProductModel {
  name: string;
  dimensions: string;
  area: string;
  occupancy: string;
  image: string;
  features: string[];
  keyFeature: string;
  price: string;
  assemblyTime: string;
  carouselImages: {
    interior: string;
    floorPlan: string;
    exterior: string;
  };
  materialList: {
    mainFrame: string;
    exteriorWall: string[];
    glass: string[];
    wall: string[];
    floor: string;
    insulation: string;
  };
  sellingPoints: string;
}

export default function ProductModels() {
  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(null);

  const models: ProductModel[] = [
    {
      name: 'M5',
      dimensions: '7.56m × 3.6m × 3.2m',
      area: '26 m²',
      occupancy: '1-2 persons',
      price: 'FROM $59,180',
      assemblyTime: '2-3 days',
      image: '/images/7f3649eeefc8.webp',
      features: ['Panoramic Views', 'Expandable Design', 'All-Terrain'],
      keyFeature: 'Perfect balance of space and portability with stunning panoramic windows for nature immersion.',
      carouselImages: {
        interior: '/images/b7c34894f8a7.webp',
        floorPlan: '/images/8f253892013a.webp',
        exterior: '/images/7f3649eeefc8.webp'
      },
      materialList: {
        mainFrame: 'Modular galvanized steel frame structure 5.0',
        exteriorWall: ['Lightweight aluminium alloy panels 1.8', 'Metal fluorocarbon paint finish'],
        glass: ['6+15A+6 hollow Low E tempered glass (modular frames)'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The M5 offers the ideal combination of living space and transportability. Its modular design allows for stunning panoramic windows while keeping each component manageable. Perfect for eco-resorts in remote locations or private retreats in challenging terrain.'
    },
    {
      name: 'M7',
      dimensions: '10m × 3.6m × 3.2m',
      area: '36 m²',
      occupancy: '2-4 persons',
      price: 'FROM $74,140',
      assemblyTime: '3 days',
      image: '/images/4f4d4413e7ef.webp',
      features: ['Maximum Space', 'Full Amenities', 'Premium Finish'],
      keyFeature: 'Extended living space with complete amenities. Ideal for families or those seeking ultimate comfort in remote locations.',
      carouselImages: {
        interior: '/images/bdcf86bd9402.webp',
        floorPlan: '/images/ff392a8a810c.webp',
        exterior: '/images/4f4d4413e7ef.webp'
      },
      materialList: {
        mainFrame: 'Modular galvanized steel frame structure 5.0',
        exteriorWall: ['Lightweight aluminium alloy panels 2.0', 'Metal fluorocarbon paint finish'],
        glass: ['6+15A+6 hollow Low E tempered glass (modular frames)'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The M7 represents the perfect fusion of spaciousness and modularity. With 36 square meters of thoughtfully designed living space, it delivers a complete home experience without compromising transportability. Whether you\'re creating a premium eco-resort in a pristine forest, a cliffside retreat overlooking the ocean, or a mountain sanctuary above the clouds, the M7 brings full-featured living to the world\'s most extraordinary places.'
    },
    {
      name: 'M9',
      dimensions: '11.5m × 3.3m × 3.2m',
      area: '38 m²',
      occupancy: '2-4 persons',
      price: 'FROM $75,460',
      assemblyTime: '3-4 days',
      image: '/images/014b41d04a33.webp',
      features: ['Maximum Capacity', 'Ultimate Comfort', 'Full Features'],
      keyFeature: 'The ultimate modular solution offering maximum space and luxury for the most demanding remote installations.',
      carouselImages: {
        interior: '/images/a0ccca3617a2.webp',
        floorPlan: '/images/e9f81dba7910.webp',
        exterior: '/images/014b41d04a33.webp'
      },
      materialList: {
        mainFrame: 'Modular galvanized steel frame structure 5.0',
        exteriorWall: ['Lightweight aluminium alloy panels 2.0', 'Metal fluorocarbon paint finish'],
        glass: ['6+15A+6 hollow Low E tempered glass (modular frames)'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The M9 represents the pinnacle of modular living solutions. With precision-engineered modules, it offers the most spacious layout in our M Series while maintaining full transportability. Perfect for permanent luxury residences in the most remote and challenging locations, offering uncompromising comfort and functionality.'
    }
  ];

  const openModal = (product: ProductModel) => {
    setSelectedProduct(product);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProduct(null);
    document.body.style.overflow = 'unset';
  };

  const getCarouselImages = (product: ProductModel) => {
    return [
      { url: product.carouselImages.interior, label: 'Interior View' },
      { url: product.carouselImages.floorPlan, label: 'Floor Plan' },
      { url: product.carouselImages.exterior, label: 'Exterior View' }
    ];
  };

  return (
    <section id="models" className="py-20 sm:py-28 lg:py-36 bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-thin mb-6 text-white">
            Choose Your <span className="text-cyan-400">M Series Model</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Three sizes designed for different space requirements, all sharing the same revolutionary modular assembly system
          </p>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
          {models.map((model, index) => (
            <div 
              key={index}
              onClick={() => openModal(model)}
              className="group relative bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.2)] transition-all duration-500 cursor-pointer"
            >
              {/* Image */}
              <div className="relative w-full h-64 overflow-hidden">
                <img 
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-4 left-4 px-4 py-2 bg-cyan-500 text-black text-sm font-medium rounded-full">
                  {model.name}
                </div>
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-cyan-400 text-sm font-semibold">{model.price}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Dimensions and Occupancy Info */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-ruler-line text-base text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Dimensions</p>
                      <p className="text-sm font-medium text-white">{model.dimensions}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-layout-grid-line text-base text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Floor Area</p>
                      <p className="text-sm font-medium text-white">{model.area}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-group-line text-base text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Occupancy</p>
                      <p className="text-sm font-medium text-white">{model.occupancy}</p>
                    </div>
                  </div>
                </div>

                {/* Key Feature */}
                <div className="mb-4 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                  <p className="text-sm text-gray-300 leading-relaxed">{model.keyFeature}</p>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {model.features.map((feature, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Click to View Details */}
                <div className="text-center py-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20 group-hover:border-cyan-500/40 group-hover:bg-cyan-500/20 transition-all">
                  <span className="text-cyan-400 text-sm font-medium">Click to View Details</span>
                  <i className="ri-arrow-right-line text-cyan-400 ml-2"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Configuration */}
        <div className="bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-settings-4-line text-2xl text-cyan-400"></i>
            </div>
            <div>
              <h3 className="text-xl font-medium text-white mb-1">Custom Modular Solutions</h3>
              <p className="text-gray-400">Need a specific configuration for your unique location? We design custom solutions.</p>
            </div>
          </div>
          <Link
            to="/contact"
            className="px-8 py-3 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium rounded-full hover:bg-cyan-500 hover:text-black transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer"
          >
            Discuss Your Project
          </Link>
        </div>
      </div>

      {/* Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={closeModal}
        >
          <div
            className="bg-[#0D1117]/95 border border-cyan-500/30 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0D1117]/95 backdrop-blur-xl border-b border-cyan-500/20 p-6 flex items-center justify-between z-10">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-4 py-1 bg-cyan-500 text-black text-sm font-medium rounded-full">{selectedProduct.name}</span>
                  <span className="text-cyan-400 font-semibold">{selectedProduct.price}</span>
                </div>
                <p className="text-gray-400 text-sm">{selectedProduct.keyFeature}</p>
              </div>
              <button
                onClick={closeModal}
                className="w-10 h-10 flex items-center justify-center bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 space-y-8">
              {/* Image Carousel - New Component */}
              <ImageCarousel 
                images={getCarouselImages(selectedProduct)} 
                className="h-96"
              />

              {/* Material List */}
              <div className="bg-black/40 border border-cyan-500/20 rounded-2xl p-6 sm:p-8">
                <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-file-list-3-line text-cyan-400"></i>
                  Technical Specifications - Material List
                </h4>
                <div className="space-y-5">
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Main Frame:</div>
                    <div className="text-gray-400 text-sm">{selectedProduct.materialList.mainFrame}</div>
                  </div>

                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Exterior Wall:</div>
                    <div className="space-y-1">
                      {selectedProduct.materialList.exteriorWall.map((item, index) => (
                        <div key={index} className="text-gray-400 text-sm">{item}</div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Glass:</div>
                    <div className="space-y-1">
                      {selectedProduct.materialList.glass.map((item, index) => (
                        <div key={index} className="text-gray-400 text-sm">{item}</div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Wall:</div>
                    <div className="space-y-1">
                      {selectedProduct.materialList.wall.map((item, index) => (
                        <div key={index} className="text-gray-400 text-sm">{item}</div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Floor:</div>
                    <div className="text-gray-400 text-sm">{selectedProduct.materialList.floor}</div>
                  </div>

                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Insulation:</div>
                    <div className="text-gray-400 text-sm">{selectedProduct.materialList.insulation}</div>
                  </div>
                </div>
              </div>

              {/* Selling Points */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-cyan-600/10 border border-cyan-500/30 rounded-2xl p-6">
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <i className="ri-lightbulb-flash-line text-cyan-400"></i>
                  Why Choose {selectedProduct.name}
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {selectedProduct.sellingPoints}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to={`/contact?product=${encodeURIComponent(selectedProduct.name)}`}
                  className="flex-1 px-8 py-4 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer text-center"
                >
                  Request Quote
                </Link>
                <button
                  onClick={closeModal}
                  className="flex-1 px-8 py-4 bg-cyan-500/10 border border-cyan-500/30 text-white text-base font-medium rounded-full hover:bg-cyan-500/20 hover:border-cyan-500/50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
